import _ from 'lodash';
import moment from 'moment-timezone';
import {daysOfWeek} from 'goodeggs-purchase-orders-sdk/procurement_schedule/enums';

export default ngInject(function(
  $scope,
  $rootScope,
  $stateParams,
  foodhub,
  toaster,
  employeeRoles,
  settings,
  Supplier,
  Catalog,
  ProcurementSchedule,
  externalUrls,
) {
  this.externalUrls = externalUrls;
  this.foodhub = foodhub;
  this.supplierSlug = $stateParams.supplierSlug;
  this.daysOfTheWeek = daysOfWeek;
  this.userHasChanges = false;
  this.userCanEdit = employeeRoles.can(
    $rootScope.credentials.google.employee.roles,
    'purchase_orders.edit',
  );

  const stopWatchingForChanges = $scope.$watch(
    'ctrl.procurementSchedule',
    (newValue, oldValue) =>
      this.userHasChanges ||
      (this.userHasChanges =
        !_.isEmpty(newValue) &&
        !_.isEmpty(oldValue) && // not if we just loaded the schedule
        !(newValue._id && !oldValue._id)), // not if we just saved the schedule
    true,
  );

  const unbindStateChange = $scope.$on('$stateChangeStart', (event) => {
    if (
      this.userHasChanges &&
      // eslint-disable-next-line no-alert
      !window.confirm('You have unsaved changes. Do you want to proceed?')
    ) {
      return event.preventDefault();
    }
    unbindStateChange();
    return stopWatchingForChanges();
  });

  Supplier.getBySlug($stateParams.supplierSlug).then((supplier) => {
    this.supplier = supplier;
    this.catalogProducts = Catalog.query({'vendor._id': this.supplier.id});

    this.procurementSchedule = ProcurementSchedule.query({
      vendorId: this.supplier.id,
      foodhubSlug: this.foodhub.slug,
    });

    return this.procurementSchedule.$promise.then(() => {
      // Instead of a list, we want an object.
      [this.procurementSchedule] = Array.from(this.procurementSchedule);
      // Create new schedule if none exists
      if (this.procurementSchedule == null) {
        this.procurementSchedule = new ProcurementSchedule({
          vendorId: this.supplier.id,
          foodhubSlug: this.foodhub.slug,
          dropoffDays: _.zipObject(
            this.daysOfTheWeek,
            _.times(this.daysOfTheWeek.length, () => ({
              enabled: false,
              confirmationDeadline: {
                leadingDays: 2,
                timeOfDay: '17:00',
              },
              dropoffWindow: {
                startAt: '05:00',
                endAt: '07:00',
              },
            })),
          ),
          dropoffDayOverrides: [],
          enableCapacityBasedAvailability: false,
          // FIXME FIXME FIXME(serhalp) This should not be hardcoded here. This prevents things like a
          // Thanksgiving preorder marketplace many days out. We should either surface this as an
          // editable field on the page (why though?) or make it a shared constant in availability-api
          // that can be easily updated via an environment variable. (Or just reuse the existing
          // `horizon` or `shoppingHorizon` constants that availability-api already uses...?)
          orderHorizon: 28,
        });
      }

      // The UI still expects this to behave like a resource,
      // so lets call it $resolved.
      this.procurementSchedule.$resolved = true;
    });
  });

  // TODO(serhalp) this is duplicated in several places - remove and call helper
  this.formatProductName = function(catalogItem) {
    if (catalogItem.sku != null) {
      return `(${catalogItem.sku}) ${catalogItem.name}`;
    }
    return catalogItem.name;
  };

  this.capacityEnabled = (dayOfWeek) =>
    this.procurementSchedule != null &&
    this.procurementSchedule.dropoffDays != null &&
    this.procurementSchedule.dropoffDays[dayOfWeek] != null
      ? this.procurementSchedule.dropoffDays[dayOfWeek].enabled
      : false;

  this.displayCapacity = (dayOfWeek, catalogItem) => {
    if (this.capacityEnabled(dayOfWeek)) {
      let capacity;
      if (catalogItem.dropoffDayCapacities != null) {
        capacity = catalogItem.dropoffDayCapacities[dayOfWeek];
      }
      if (capacity == null) {
        capacity = catalogItem.defaultCapacity;
      }
      if (capacity == null) {
        capacity = 0;
      }
      return capacity;
    }
    return '';
  };

  this.hasUpcomingOverrides = (dayOfWeek, catalogItem) => {
    const overrideInputs = [
      this.procurementSchedule.dropoffDayOverrides,
      (catalogItem != null ? catalogItem.dropoffDayCapacityOverrides : undefined) != null
        ? catalogItem != null
          ? catalogItem.dropoffDayCapacityOverrides
          : undefined
        : [],
    ];
    const [hasDayOverrides, hasCapacityOverrides] = Array.from(
      Array.from(overrideInputs).map((overrides) =>
        _(overrides)
          .filter(function(param) {
            if (param == null) {
              param = {};
            }
            const {dropoffDay} = param;
            if (!dropoffDay) {
              return false;
            }
            return moment(dropoffDay).isAfter(moment());
          })
          .some(
            ({dropoffDay}) =>
              moment(dropoffDay)
                .format('dd')
                .toUpperCase() === dayOfWeek,
          ),
      ),
    );
    if (catalogItem != null) {
      return hasCapacityOverrides;
    }
    return hasDayOverrides;
  };

  this.displayOverrides = (dayOfWeek, catalogItem) => {
    let capacityOverrides;
    const exclusions = _(this.procurementSchedule.dropoffDayOverrides)
      .filter(
        ({dropoffDay, schedule}) =>
          !schedule.enabled &&
          moment(dropoffDay)
            .format('dd')
            .toUpperCase() === dayOfWeek &&
          moment(dropoffDay).isAfter(moment()),
      )
      .map(({dropoffDay}) => moment(dropoffDay).format('ddd LL'))
      .value();

    if (catalogItem != null) {
      capacityOverrides = catalogItem.dropoffDayCapacityOverrides
        .filter(
          ({dropoffDay}) =>
            moment(dropoffDay)
              .format('dd')
              .toUpperCase() === dayOfWeek && moment(dropoffDay).isAfter(moment()),
        )
        .map(({dropoffDay, capacity}) => `${moment(dropoffDay).format('ddd LL')} (${capacity})`);
    }

    let out = '';
    if (exclusions.length) {
      out += `<div>No drop-offs on dates: <pre>${exclusions.join('\n').trim()}\n</pre></div>`;
    }
    if (capacityOverrides != null ? capacityOverrides.length : undefined) {
      out += `<div>Capacity may change on dates: <pre>${capacityOverrides
        .join('\n')
        .trim()}</pre></div>`;
    }
    return out;
  };

  this.save = () => {
    if (this.procurementSchedule == null) {
      return;
    }
    if (this.procurementSchedule.enableCapacityBasedAvailability) {
      if (
        // eslint-disable-next-line no-alert
        !window.confirm(
          'Modifying a procurement schedule with capacity-based availability enabled may have an impact on current market availability. Are you sure you want to save?',
        )
      ) {
        return;
      }
    }
    const persist = this.procurementSchedule._id != null ? '$update' : '$save';
    return this.procurementSchedule[persist]()
      .then(() => toaster.success('Saved schedule'))
      .catch(toaster.serverFailCB())
      .then(() => (this.userHasChanges = false));
  };

  return this; // http://stackoverflow.com/questions/28953289/using-controller-as-with-the-ui-router-isnt-working-as-expected
});
